// 权限审计日志写入工具 (V2 权限基础设施 M1a)
//
// 设计:
//   - logAction — 通用操作日志
//   - logFieldAccess — 敏感字段访问 (FIELD_ACCESS action)
//   - logExport — 导出审计 (含 fields / rowCount / fileHash / watermark)
//   - 自动注入 empNo / roleCodes / IP / UA / sessionId
//   - 留存策略按 sensitivityLevel
//
// 注意: 哈希链由 permAuditLogStore.appendLog 内部维护

import { permAuditLogStore, type PermAuditAction, type PermAuditLog, RETENTION_DAYS_BY_LEVEL } from '@/stores/permAuditLogStore'
import { fieldPermissionStore, type SensitivityLevel } from '@/stores/fieldPermissionStore'
import { useCurrentUser } from '@/composables/useCurrentUser'

// ─── 上下文采集 ─────────────────────────────────────────────────────────
interface RuntimeContext {
  ip: string
  userAgent: string
  sessionId: string
}

let _sessionId = ''
function getSessionId(): string {
  if (_sessionId) return _sessionId
  try {
    _sessionId = sessionStorage.getItem('royal-tiger-sid') || ''
    if (!_sessionId) {
      _sessionId = 's-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8)
      sessionStorage.setItem('royal-tiger-sid', _sessionId)
    }
  } catch {
    _sessionId = 's-' + Date.now().toString(36)
  }
  return _sessionId
}

function getRuntimeContext(): RuntimeContext {
  return {
    ip: '127.0.0.1',   // mock, 浏览器无法直接拿到 IP
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
    sessionId: getSessionId(),
  }
}

// ─── 留存计算 ─────────────────────────────────────────────────────────
export function getRetentionDays(sensitivityLevel?: SensitivityLevel): number {
  if (!sensitivityLevel) return RETENTION_DAYS_BY_LEVEL.L2
  return RETENTION_DAYS_BY_LEVEL[sensitivityLevel]
}

// ─── 通用 logAction ────────────────────────────────────────────────────
export interface LogActionInput {
  module: string
  subModule?: string
  action: PermAuditAction
  target: { type: string; id: string; summary?: string }
  before?: any
  after?: any
  diff?: { field: string; from: any; to: any }[]
  status?: 'OK' | 'DENIED' | 'ERROR'
  errorMessage?: string
  responseTimeMs?: number
  sensitivityLevel?: SensitivityLevel
  legalEntity?: string
  exportDetail?: PermAuditLog['exportDetail']
}

export function logAction(opts: LogActionInput): PermAuditLog | undefined {
  try {
    const user = useCurrentUser()
    const ctx = getRuntimeContext()
    return permAuditLogStore.appendLog({
      empNo: user.empNo,
      empName: user.empName,
      roleCodes: user.roleCodes,
      module: opts.module,
      subModule: opts.subModule,
      action: opts.action,
      targetType: opts.target.type,
      targetId: opts.target.id,
      targetSummary: opts.target.summary,
      before: opts.before,
      after: opts.after,
      diff: opts.diff,
      ip: ctx.ip,
      userAgent: ctx.userAgent,
      sessionId: ctx.sessionId,
      status: opts.status || 'OK',
      errorMessage: opts.errorMessage,
      responseTimeMs: opts.responseTimeMs,
      sensitivityLevel: opts.sensitivityLevel,
      legalEntity: opts.legalEntity,
      exportDetail: opts.exportDetail,
    })
  } catch (e) {
    console.warn('[permAuditLogger] logAction failed', e)
    return undefined
  }
}

// ─── 字段访问日志 ─────────────────────────────────────────────────────
export function logFieldAccess(module: string, field: string, recordId: string, opts?: { context?: string }): PermAuditLog | undefined {
  const rule = fieldPermissionStore.getRule(module, field)
  // 只对 L3 / L4 / audit=true 字段记录
  if (rule && !rule.audit && rule.sensitivityLevel !== 'L3' && rule.sensitivityLevel !== 'L4') {
    return undefined
  }
  return logAction({
    module,
    action: 'FIELD_ACCESS',
    target: { type: 'Field', id: `${module}.${field}#${recordId}`, summary: `${rule?.fieldLabel || field} (${rule?.sensitivityLevel || '?'}) on ${recordId}${opts?.context ? ' in ' + opts.context : ''}` },
    sensitivityLevel: rule?.sensitivityLevel,
  })
}

// ─── 导出审计 ─────────────────────────────────────────────────────────
export interface LogExportInput {
  module: string
  subModule?: string
  fields: string[]
  rowCount: number
  filterDescription?: string
  fileHash?: string
  watermark?: string
  legalEntity?: string
  targetSummary?: string
}

export function logExport(opts: LogExportInput): PermAuditLog | undefined {
  // 自动产生 watermark = empNo + yyyyMMdd
  const user = useCurrentUser()
  const date = new Date()
  const ymd = `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}`
  const watermark = opts.watermark || `${user.empNo}-${ymd}`
  // 计算 fileHash 占位
  const fileHash = opts.fileHash || Math.random().toString(36).slice(2, 14)

  // 字段最高敏感度作为整条 log 的级别
  let highestLevel: SensitivityLevel = 'L1'
  for (const f of opts.fields) {
    const r = fieldPermissionStore.getRule(opts.module, f)
    if (r) {
      const order: SensitivityLevel[] = ['L1', 'L2', 'L3', 'L4']
      if (order.indexOf(r.sensitivityLevel) > order.indexOf(highestLevel)) {
        highestLevel = r.sensitivityLevel
      }
    }
  }

  return logAction({
    module: opts.module,
    subModule: opts.subModule,
    action: 'EXPORT',
    target: {
      type: 'Export', id: `exp-${Date.now()}`,
      summary: opts.targetSummary || `导出 ${opts.module} ${opts.rowCount} 行 / ${opts.fields.length} 字段`,
    },
    sensitivityLevel: highestLevel,
    legalEntity: opts.legalEntity,
    exportDetail: {
      fields: opts.fields,
      rowCount: opts.rowCount,
      fileHash,
      watermark,
      filterDescription: opts.filterDescription,
    },
  })
}

// ─── 登录/登出 ────────────────────────────────────────────────────────
export function logLogin(empNo: string, empName: string, opts?: { ssoProvider?: string; success?: boolean; errorMessage?: string }): PermAuditLog | undefined {
  const ctx = getRuntimeContext()
  return permAuditLogStore.appendLog({
    empNo, empName, roleCodes: [],
    module: 'sys', action: 'LOGIN',
    targetType: 'Session', targetId: ctx.sessionId,
    targetSummary: opts?.ssoProvider ? `SSO 登录 (${opts.ssoProvider})` : '密码登录',
    ip: ctx.ip, userAgent: ctx.userAgent, sessionId: ctx.sessionId,
    status: opts?.success === false ? 'DENIED' : 'OK',
    errorMessage: opts?.errorMessage,
    sensitivityLevel: 'L1',
  })
}

export function logLogout(empNo: string, empName: string): PermAuditLog | undefined {
  const ctx = getRuntimeContext()
  return permAuditLogStore.appendLog({
    empNo, empName, roleCodes: [],
    module: 'sys', action: 'LOGOUT',
    targetType: 'Session', targetId: ctx.sessionId,
    ip: ctx.ip, userAgent: ctx.userAgent, sessionId: ctx.sessionId,
    status: 'OK', sensitivityLevel: 'L1',
  })
}

// ─── BreakGlass 专用 ─────────────────────────────────────────────────
export function logBreakGlass(opts: {
  triggerEmpNo: string
  triggerEmpName: string
  witnesses: string[]
  reason: string
  bgId: string
}): PermAuditLog | undefined {
  const ctx = getRuntimeContext()
  return permAuditLogStore.appendLog({
    empNo: opts.triggerEmpNo, empName: opts.triggerEmpName, roleCodes: [],
    module: 'sys', action: 'BREAK_GLASS',
    targetType: 'BreakGlass', targetId: opts.bgId,
    targetSummary: opts.reason,
    before: undefined, after: { witnesses: opts.witnesses },
    ip: ctx.ip, userAgent: ctx.userAgent, sessionId: ctx.sessionId,
    status: 'OK', sensitivityLevel: 'L4',
  })
}

// ─── Grant / Revoke 专用 ────────────────────────────────────────────
export function logGrant(opts: {
  granteeEmpNo: string
  grantSummary: string
  tempGrantId: string
}): PermAuditLog | undefined {
  return logAction({
    module: 'sys',
    action: 'GRANT',
    target: { type: 'TempGrant', id: opts.tempGrantId, summary: `授权 ${opts.granteeEmpNo}: ${opts.grantSummary}` },
    sensitivityLevel: 'L3',
  })
}

export function logRevoke(opts: { tempGrantId: string; reason?: string }): PermAuditLog | undefined {
  return logAction({
    module: 'sys',
    action: 'REVOKE',
    target: { type: 'TempGrant', id: opts.tempGrantId, summary: '撤销临时授权: ' + (opts.reason || '') },
    sensitivityLevel: 'L3',
  })
}

// ─── Diff 计算 helper ────────────────────────────────────────────────
export function computeDiff(before: Record<string, any> | undefined, after: Record<string, any> | undefined): { field: string; from: any; to: any }[] {
  if (!before || !after) return []
  const out: { field: string; from: any; to: any }[] = []
  const keys = new Set([...Object.keys(before), ...Object.keys(after)])
  for (const k of keys) {
    if (JSON.stringify(before[k]) !== JSON.stringify(after[k])) {
      out.push({ field: k, from: before[k], to: after[k] })
    }
  }
  return out
}

export default {
  logAction,
  logFieldAccess,
  logExport,
  logLogin,
  logLogout,
  logBreakGlass,
  logGrant,
  logRevoke,
  computeDiff,
  getRetentionDays,
}
